// Scythe Disc look (owner 2026-11-02: 'update the scythe disk shape to look more like a circular disk, default size should be
// smaller and grow to a much larger disk size'; then 'yellow and reduce bloom/glow'). A thin, flat, spinning yellow disc:
// small pale hub, gold-to-yellow body with faint spinning grooves, and a crisp bright cutting rim with a slightly ragged, flickering outer
// edge, and (2026-11-03) a glowing white-to-yellow inside. Hard cel bands, not a soft blob. Geometry radius 1 (scaled to the sim's hit radius in view.ts); spun in sync().
import * as THREE from 'three/webgpu';
import { atan, color, float, length, mix, mx_noise_float, smoothstep, time, vec3, positionLocal } from 'three/tsl';

export function buildDiscGeometry(): THREE.BufferGeometry {
  // a very thin cylinder: the caps carry the look, the edge gives it a sliver of thickness from the tilted camera
  return new THREE.CylinderGeometry(1, 1, 0.05, 56, 1);
}

export function buildDiscMaterial(): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ fog: false, transparent: true, depthWrite: false, side: THREE.DoubleSide });
  const p = positionLocal, rad = length(p.xz), ang = atan(p.z, p.x);
  // faint spinning grooves: swirled radial streaks (the mesh spins, the pattern also drifts so it shimmers)
  const groove = smoothstep(float(0.55), float(0.95), ang.mul(9).add(rad.mul(7)).add(time.mul(4)).sin()).mul(smoothstep(float(0.3), float(0.5), rad));
  // flickering ragged outer edge
  const n = mx_noise_float(vec3(ang.mul(6), time.mul(9), float(0))).mul(0.035);
  const edge = float(0.985).add(n);
  // owner 2026-11-02: yellow, and far less bloom/glow so it reads as a flat disc, not a ball of energy
  const core = smoothstep(float(0.16), float(0.08), rad);                       // small hub, not a glowing ball
  const rim = smoothstep(float(0.84), float(0.88), rad).mul(smoothstep(edge, edge.sub(0.035), rad)); // crisp cutting rim band
  let col = mix(color(0xd9a400), color(0xffd52e), smoothstep(float(0.15), float(0.8), rad)); // deep gold → yellow
  col = mix(col, color(0xffeb85), groove.mul(0.45));
  col = mix(col, color(0xfff3b8), rim);
  col = mix(col, color(0xfff8dc), core);
  // owner 2026-11-03: 'make the inside of the scythe discs bright white, yellow, kind of glowing' — a white-hot centre fading
  // through bright yellow out to ~70% of the radius, pushed over the bloom threshold so the inside glows; the gold cutting rim
  // and the ragged edge stay as they were so it still reads as a disc
  const inner = smoothstep(float(0.72), float(0.12), rad);                      // 1 at the centre → 0 by 0.72 R
  const glowCol = mix(color(0xffe23a), color(0xffffff), smoothstep(float(0.5), float(0.05), rad)); // yellow → white-hot centre
  col = mix(col, glowCol.mul(1.9), inner.mul(0.92));
  m.colorNode = col.mul(0.95);
  m.opacityNode = smoothstep(edge, edge.sub(0.025), rad).mul(0.96);
  return m;
}
